// ═══════════════════════════════════════════════════════════════════════
// Paramètres › Réglementation › un pack (Lot 2A) — par exemple « Sénégal — Temps & Congés — V1 ».
// Lu par la DRH et le juriste : en ouvrant le pack, on distingue tout de suite ce qui est CONFIRMÉ,
// À CONFIRMER, laissé à une DÉCISION SOGAS, les DONNÉES MANQUANTES et l'EFFET SIMULÉ. Chaque règle porte
// son niveau (loi, convention, accord), sa source, sa citation et dit si un calcul l'applique déjà.
// Aucun code technique à l'écran. Les droits sont contrôlés par le serveur (IAM du Centre).
// ═══════════════════════════════════════════════════════════════════════
import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Plus, Send, Landmark, PlayCircle, ShieldCheck, HelpCircle, Building2, Database, BarChart3, Trash2 } from 'lucide-react';
import Layout from '../../components/Layout';
import { EnTetePage, Carte, Bouton, BandeauInfo, Badge, Onglets, Squelette, formaterDate } from '../../design/composants';
import { useChargement, Fenetre, Champ, PiedFenetre } from '../temps/reglagesCommun';
import {
  lireCfg, ecrireCfg, messageErreur, NavParametres, NIVEAUX, SOURCES, CONFIANCES, SLUG_DOMAINE,
  BadgeStatutRegle, BadgeBranchement, FenetreValider, FenetreMotifCfg,
} from './configurationCommun';

const TON_CONFIRMATION = { CONFIRME: 'succes', A_CONFIRMER: 'alerte', DECISION_SOGAS: 'info' };
const TON_NIVEAU = { LOI: 'info', CONVENTION: 'neutre', ACCORD: 'neutre', SOGAS: 'neutre' };
const LIBELLE_DOMAINE = { TEMPS: 'Temps et pointage', HEURES_SUP: 'Heures supplémentaires', CONGES: 'Congés et absences', RETENUES: 'Retenues sur salaire', DISPOSITIFS: 'Dispositifs sociaux' };
const LIBELLE_PAYS = { SN: 'Sénégal' };
const CAUSES = {
  ANCIENNETE_SERAS_NON_TRANCHEE: 'Entrée avant 2003 : la reprise de l\'ancienneté SERAS n\'est pas tranchée',
  COMPTE_A_QUALIFIER: 'Fiche « à qualifier » : identité du salarié non établie',
  PALIER_SENSIBLE_A_LA_DATE: 'Date d\'entrée approximative qui peut changer le palier d\'ancienneté',
  DATE_ENTREE_CONTREDITE: 'Date d\'entrée contredite par l\'affectation d\'embauche',
  ENFANTS_NON_DETAILLES: 'Salariée avec des enfants déclarés mais non détaillés',
  DATE_ENTREE_MANQUANTE: 'Date d\'entrée absente',
  REGLE_MANQUANTE: 'Règle indispensable absente du pack (acquisition ou ouverture du droit)',
};
const RESERVES = {
  DATE_ENTREE_A_CONFIRMER: 'Date d\'entrée à confirmer (1er du mois, dimanche, férié, date partagée, période SERAS)',
  DATE_NAISSANCE_MANQUANTE: 'Date de naissance absente',
  SUSPENSION_NON_CLASSEE: 'Absence sans catégorie de service effectif (ex. congé sans solde à qualifier)',
  GENRE_MANQUANT: 'Genre absent', ENFANTS_INCONNUS: 'Enfants non renseignés', ENFANT_SANS_DATE: 'Enfant sans date de naissance',
  ANCIENNETE_REPRISE: 'Ancienneté reprise à justifier',
};
const QUALITES = {
  PROBABLE_REELLE: 'Probablement réelle', REPRISE_APPROXIMATIVE: 'Issue d\'une reprise approximative',
  IMPOSSIBLE_A_DETERMINER: 'Impossible à déterminer', A_CONFIRMER: 'À confirmer', MANQUANTE: 'Absente',
};
const ANOMALIES = {
  dates_premier_du_mois: 'Dates d\'entrée au 1er du mois', dates_dimanche_ou_ferie: 'Dates d\'entrée un dimanche ou un férié',
  entrees_avant_2003: 'Entrées avant 2003 (question SERAS)', comptes_a_qualifier: 'Fiches à qualifier',
  enfants_declares_differents_de_la_table: 'Nombre d\'enfants déclaré différent des enfants détaillés',
  dates_naissance_absentes: 'Dates de naissance absentes', salaries_sans_horaire: 'Salariés sans horaire (repos hebdomadaire inconnu)',
  jours_feries_en_base: 'Jours fériés enregistrés (calendrier de l\'année)',
};

const BadgeConfirmation = ({ c }) => (c ? <Badge ton={TON_CONFIRMATION[c.code] || 'neutre'} point>{c.libelle}</Badge> : null);

/** Valeur d'une règle : barème et matrice dépliables, sinon texte. */
function ValeurRegle({ r }) {
  if (r.type === 'BAREME' && Array.isArray(r.valeur_structuree)) {
    return (
      <table className="rh-tableau" style={{ fontSize: 13, minWidth: 0 }}>
        <thead><tr><th>{r.bareme.seuil.libelle}</th><th>{r.bareme.valeur.libelle}</th></tr></thead>
        <tbody>{r.valeur_structuree.map((p) => <tr key={p.seuil}><td>{p.seuil} ans</td><td>+{String(p.valeur).replace('.', ',')} j</td></tr>)}</tbody>
      </table>
    );
  }
  if (r.type === 'MATRICE' && r.valeur_structuree) {
    const rep = (v) => (v === true ? <Badge ton="succes">Oui</Badge> : v === false ? <Badge ton="danger">Non</Badge> : <Badge ton="alerte">À confirmer</Badge>);
    return (
      <details>
        <summary style={{ cursor: 'pointer' }}>{r.cases_a_confirmer ? `Tableau (${r.cases_a_confirmer} case(s) à confirmer)` : 'Tableau complet'}</summary>
        <div className="rh-conteneur-tableau"><table className="rh-tableau" style={{ fontSize: 13, marginTop: 6 }}>
          <thead><tr><th>Situation</th>{r.matrice.colonnes.map((c) => <th key={c.code}>{c.libelle}</th>)}</tr></thead>
          <tbody>{r.matrice.lignes.map((l) => (
            <tr key={l.code}><td>{l.libelle}</td>{r.matrice.colonnes.map((c) => <td key={c.code}>{rep(r.valeur_structuree[l.code]?.[c.code])}</td>)}</tr>
          ))}</tbody>
        </table></div>
      </details>
    );
  }
  return <strong>{r.valeur}</strong>;
}

/** Une règle du pack, lisible par la DRH et le juriste. */
function LigneRegle({ r, peutRetirer, onRetirer }) {
  return (
    <tr>
      <td style={{ minWidth: 220 }}>
        <div style={{ fontWeight: 600 }}>{r.libelle}</div>
        {r.aide && <div style={{ fontSize: 12, color: 'var(--sg-texte-2)' }}>{r.aide}</div>}
      </td>
      <td><ValeurRegle r={r} /></td>
      <td><Badge ton={TON_NIVEAU[r.niveau]}>{r.niveau_libelle}</Badge></td>
      <td><BadgeConfirmation c={r.confirmation} /></td>
      <td style={{ minWidth: 240, fontSize: 13 }}>
        <div>{r.reference_juridique || '—'}</div>
        {(r.citation || r.question) && (
          <details style={{ marginTop: 4 }}>
            <summary style={{ cursor: 'pointer' }}>Texte et question</summary>
            {r.citation && <p style={{ margin: '4px 0', fontStyle: 'italic' }}>{r.citation}</p>}
            {r.question && <p style={{ margin: '4px 0', color: 'var(--sg-alerte, #9a6700)' }}><HelpCircle size={13} aria-hidden="true" /> {r.question}</p>}
          </details>
        )}
      </td>
      <td style={{ fontSize: 12, minWidth: 160 }}>
        <BadgeBranchement branchement={r.effet} />
        <div style={{ color: 'var(--sg-texte-2)', marginTop: 2 }}>{r.effet.etat === 'NON_BRANCHE' ? (r.effet.libelle.split('prévue : ')[1] || '') : 'Déjà lue par un calcul'}</div>
        {r.effet.ecart_moteur && <div style={{ color: 'var(--sg-danger, #b42318)', marginTop: 2 }}>Activation après le Lot 3</div>}
      </td>
      {peutRetirer && <td><Bouton petit variante="discret" Icone={Trash2} aria-label={`Retirer ${r.libelle}`} onClick={() => onRetirer(r)}>Retirer</Bouton></td>}
    </tr>
  );
}

/** Explication « Pourquoi ce résultat ? » d'un salarié (simulation). */
function Explication({ exemple }) {
  return (
    <details className="rh-carte" style={{ padding: 10, marginBottom: 8 }}>
      <summary style={{ cursor: 'pointer', fontSize: 14 }}>
        <strong>{exemple.nom}</strong> · {exemple.site || '—'} · date d'entrée : {QUALITES[exemple.qualite_date_entree] || exemple.qualite_date_entree}
        {exemple.resultat ? ` · ${String(exemple.resultat.droit_ouverture.total).replace('.', ',')} j le ${formaterDate(exemple.resultat.droit_ouverture.date)}` : ' · non calculable'}
      </summary>
      <p style={{ margin: '8px 0 4px', fontWeight: 600 }}>Pourquoi ce résultat ?</p>
      <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13 }}>
        {exemple.explication.map((e, i) => (
          <li key={i} style={{ marginBottom: 2 }}>
            {e.libelle} : <strong>{e.valeur}</strong>{e.detail ? ` — ${e.detail}` : ''}
            {e.regle && <span style={{ color: 'var(--sg-texte-2)' }}> ({e.regle.reference || e.regle.libelle}{e.regle.statut !== 'CONFIRME' ? ', non confirmée' : ''})</span>}
          </li>
        ))}
      </ul>
      {(exemple.blocages || []).length > 0 && <p style={{ margin: '6px 0 0', fontSize: 13 }}>Bloqué : {exemple.blocages.map((b) => CAUSES[b] || b).join(' ; ')}</p>}
    </details>
  );
}

function Chiffre({ valeur, libelle, ton }) {
  return (
    <div className="rh-carte" style={{ padding: 12, minWidth: 140, flex: '1 1 140px' }}>
      <div style={{ fontSize: 24, fontWeight: 700, color: ton ? `var(--sg-${ton}, inherit)` : undefined }}>{valeur}</div>
      <div style={{ fontSize: 13, color: 'var(--sg-texte-2)' }}>{libelle}</div>
    </div>
  );
}

/** Ajouter une règle au pack : niveau, valeur (nombre, liste, barème, tableau), source, citation, question ouverte. */
function FenetreAjouterRegle({ pack, onFermer, onTermine }) {
  const [regles, setRegles] = useState([]);
  useEffect(() => {
    Promise.all((pack.domaines.length ? pack.domaines : ['TEMPS', 'CONGES', 'HEURES_SUP']).map((d) => lireCfg(`/domaines/${d}/regles`).then((x) => x.regles).catch(() => [])))
      .then((l) => setRegles(l.flat().filter((x) => x.niveau_defaut !== 'SOGAS' || x.code === 'CONGE_ACQUISITION_MENSUELLE_JOURS')));
  }, [pack.domaines]);
  const niveaux = NIVEAUX.filter(([k]) => k !== 'SOGAS' && NIVEAUX.findIndex(([x]) => x === k) >= NIVEAUX.findIndex(([x]) => x === pack.niveau));
  const [f, setF] = useState({ code: '', niveau: pack.niveau, valeur: '', source: 'TEXTE_LEGAL', reference_juridique: '', niveau_confiance: 'A_VALIDER_JURISTE', citation: '', question: '', motif: '' });
  const [erreur, setErreur] = useState('');
  const r = regles.find((x) => x.code === f.code);
  const maj = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const choisir = (code) => {
    const x = regles.find((y) => y.code === code);
    let valeur = '';
    if (x?.type === 'BAREME') valeur = [{ seuil: '', valeur: '' }];
    if (x?.type === 'MATRICE') valeur = Object.fromEntries(x.matrice.lignes.map((l) => [l.code, Object.fromEntries(x.matrice.colonnes.map((c) => [c.code, null]))]));
    setF({ ...f, code, valeur, niveau: x && niveaux.some(([k]) => k === x.niveau_defaut) ? x.niveau_defaut : pack.niveau });
  };
  const pret = r && f.motif.trim().length >= 3 && (r.type === 'BAREME' ? f.valeur.every((p) => p.seuil !== '' && p.valeur !== '') : r.type === 'MATRICE' ? true : f.valeur !== '');
  const valeurEnvoyee = () => {
    if (!r) return null;
    if (r.type === 'BAREME') return f.valeur.map((p) => ({ seuil: Number(p.seuil), valeur: Number(p.valeur) }));
    if (['DUREE', 'NOMBRE'].includes(r.type)) return Number(f.valeur);
    return f.valeur;
  };
  return (
    <Fenetre titre={`Ajouter une règle — ${pack.libelle}`} Icone={Plus} onFermer={onFermer} large>
      {erreur && <BandeauInfo ton="danger">{erreur}</BandeauInfo>}
      <p style={{ margin: '0 0 12px', fontSize: 13 }}>La règle prend la date d'effet du pack ({formaterDate(pack.date_debut)}). Une règle de loi ou de convention cite toujours son texte ; elle ne sera activable qu'une fois confirmée.</p>
      <Champ libelle="Règle" requis>
        <select className="rh-champ-saisie" value={f.code} onChange={(e) => choisir(e.target.value)}>
          <option value="">Choisir…</option>
          {regles.map((x) => <option key={x.code} value={x.code}>{LIBELLE_DOMAINE[x.domaine]} — {x.libelle}</option>)}
        </select>
      </Champ>
      {r?.aide && <p style={{ margin: '-6px 0 10px', fontSize: 13, color: 'var(--sg-texte-2)' }}>{r.aide}</p>}
      {r && (
        <Champ libelle="Niveau" requis>
          <select className="rh-champ-saisie" value={f.niveau} onChange={maj('niveau')}>{niveaux.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
        </Champ>
      )}
      {r && r.type === 'BAREME' && (
        <fieldset style={{ border: 0, padding: 0, margin: '0 0 12px' }}>
          <legend style={{ fontSize: 13, fontWeight: 500 }}>Paliers (croissants)</legend>
          {f.valeur.map((p, i) => (
            <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
              <input type="number" className="rh-champ-saisie" style={{ maxWidth: 160 }} placeholder={r.bareme.seuil.libelle} aria-label={`${r.bareme.seuil.libelle}, palier ${i + 1}`}
                value={p.seuil} onChange={(e) => setF({ ...f, valeur: f.valeur.map((x, j) => (j === i ? { ...x, seuil: e.target.value } : x)) })} />
              <input type="number" step="0.5" className="rh-champ-saisie" style={{ maxWidth: 160 }} placeholder={r.bareme.valeur.libelle} aria-label={`${r.bareme.valeur.libelle}, palier ${i + 1}`}
                value={p.valeur} onChange={(e) => setF({ ...f, valeur: f.valeur.map((x, j) => (j === i ? { ...x, valeur: e.target.value } : x)) })} />
              {f.valeur.length > 1 && <Bouton petit variante="discret" onClick={() => setF({ ...f, valeur: f.valeur.filter((_, j) => j !== i) })}>Retirer</Bouton>}
            </div>
          ))}
          <Bouton petit Icone={Plus} onClick={() => setF({ ...f, valeur: [...f.valeur, { seuil: '', valeur: '' }] })}>Ajouter un palier</Bouton>
        </fieldset>
      )}
      {r && r.type === 'MATRICE' && (
        <div className="rh-conteneur-tableau" style={{ marginBottom: 12 }}><table className="rh-tableau" style={{ fontSize: 13 }}>
          <thead><tr><th>Situation</th>{r.matrice.colonnes.map((c) => <th key={c.code}>{c.libelle}</th>)}</tr></thead>
          <tbody>{r.matrice.lignes.map((l) => (
            <tr key={l.code}><td>{l.libelle}</td>{r.matrice.colonnes.map((c) => (
              <td key={c.code}>
                <select className="rh-champ-saisie" aria-label={`${l.libelle} — ${c.libelle}`} value={f.valeur[l.code][c.code] === null ? '' : String(f.valeur[l.code][c.code])}
                  onChange={(e) => setF({ ...f, valeur: { ...f.valeur, [l.code]: { ...f.valeur[l.code], [c.code]: e.target.value === '' ? null : e.target.value === 'true' } } })}>
                  <option value="">À confirmer</option><option value="true">Oui</option><option value="false">Non</option>
                </select>
              </td>
            ))}</tr>
          ))}</tbody>
        </table></div>
      )}
      {r && r.type === 'LISTE' && (
        <Champ libelle="Valeur" requis>
          <select className="rh-champ-saisie" value={f.valeur} onChange={maj('valeur')}><option value="">Choisir…</option>
            {r.valeurs.map((c) => <option key={c} value={c}>{(r.libelles_valeurs && r.libelles_valeurs[c]) || c}</option>)}</select>
        </Champ>
      )}
      {r && r.type === 'HEURE' && <Champ libelle="Heure" requis><input type="time" className="rh-champ-saisie" value={f.valeur} onChange={maj('valeur')} style={{ maxWidth: 160 }} /></Champ>}
      {r && ['DUREE', 'NOMBRE'].includes(r.type) && (
        <Champ libelle={`Valeur${r.unite_affichage ? ` (${r.unite_affichage.toLowerCase().replace(/_/g, ' ')})` : ''}`} requis>
          <input type="number" min="0" step={r.entier ? 1 : 0.5} className="rh-champ-saisie" value={f.valeur} onChange={maj('valeur')} style={{ maxWidth: 160 }} />
        </Champ>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
        <Champ libelle="Source" requis><select className="rh-champ-saisie" value={f.source} onChange={maj('source')}>{Object.entries(SOURCES).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></Champ>
        <Champ libelle="Confirmation" requis><select className="rh-champ-saisie" value={f.niveau_confiance} onChange={maj('niveau_confiance')}>{Object.entries(CONFIANCES).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></Champ>
      </div>
      <Champ libelle="Référence précise (texte, article)" requis><input className="rh-champ-saisie" value={f.reference_juridique} onChange={maj('reference_juridique')} /></Champ>
      <Champ libelle="Citation du texte"><textarea className="rh-champ-saisie" rows={2} value={f.citation} onChange={maj('citation')} /></Champ>
      <Champ libelle="Question à confirmer (juriste, DRH)"><textarea className="rh-champ-saisie" rows={2} value={f.question} onChange={maj('question')} /></Champ>
      <Champ libelle="Motif" requis><textarea className="rh-champ-saisie" rows={2} value={f.motif} onChange={maj('motif')} /></Champ>
      <PiedFenetre>
        <Bouton onClick={onFermer}>Retour</Bouton>
        <Bouton variante="primaire" Icone={Plus} disabled={!pret}
          onClick={async () => { try { await ecrireCfg('post', '/versions', { ...f, pack_id: pack.id, valeur: valeurEnvoyee() }); onTermine(); } catch (e) { setErreur(messageErreur(e)); } }}>Ajouter</Bouton>
      </PiedFenetre>
    </Fenetre>
  );
}

export default function PackReglementairePage() {
  const { id } = useParams();
  const dom = useChargement(() => lireCfg('/domaines'), []);
  const { donnees: p, erreur, recharger } = useChargement(() => lireCfg(`/packs/${id}`), [id]);
  const [onglet, setOnglet] = useState('regles');
  const [sous, setSous] = useState(null);
  const [message, setMessage] = useState('');
  const [erreurAction, setErreurAction] = useState('');
  const [enCours, setEnCours] = useState(false);
  const droits = dom.donnees?.droits?.domaines?.REGLEMENTATION;
  const fini = async (m) => { setSous(null); setMessage(m); setErreurAction(''); await recharger(); };
  const agir = async (fn, m) => { setEnCours(true); try { setErreurAction(''); await fn(); await fini(m); } catch (e) { setErreurAction(messageErreur(e)); } finally { setEnCours(false); } };

  if (!p) {
    return <Layout><div className="rh-page"><NavParametres actif="reglementation" />{erreur ? <BandeauInfo ton="danger">{erreur}</BandeauInfo> : <Squelette hauteur={200} />}</div></Layout>;
  }
  const s = p.simulation;
  const decisionsARenseigner = p.decisions_sogas.filter((d) => !d.reglee);
  const domaines = [...new Set(p.regles.map((r) => r.domaine))];
  const onglets = [
    { cle: 'regles', libelle: 'Règles du pack', Icone: ShieldCheck, compte: p.regles.length },
    { cle: 'sogas', libelle: 'Décisions SOGAS', Icone: Building2, compte: decisionsARenseigner.length },
    { cle: 'donnees', libelle: 'Données', Icone: Database },
    { cle: 'simulation', libelle: 'Effet simulé', Icone: BarChart3 },
  ];
  return (
    <Layout>
      <div className="rh-page">
        <EnTetePage fil={[{ libelle: 'Paramètres', vers: '/parametres' }, { libelle: 'Réglementation', vers: '/parametres/reglementation' }, { libelle: p.libelle }]}
          titre={p.libelle}
          sousTitre={`Version ${p.version} · ${LIBELLE_PAYS[p.pays] || p.pays || 'Pays non précisé'} · ${(p.domaines || []).map((d) => LIBELLE_DOMAINE[d] || d).join(', ')} · à partir du ${formaterDate(p.date_debut)}${p.date_fin ? ` jusqu'au ${formaterDate(p.date_fin)}` : ''}`}
          actions={<BadgeStatutRegle statut={p.statut} />} />
        <NavParametres actif="reglementation" />
        {message && <BandeauInfo ton="succes">{message}</BandeauInfo>}
        {erreurAction && <BandeauInfo ton="danger">{erreurAction}</BandeauInfo>}

        {p.activable.possible
          ? <BandeauInfo ton="succes" titre="Toutes les règles sont confirmées">Le pack peut être simulé puis soumis à la validation d'une autre personne habilitée.</BandeauInfo>
          : (
            <BandeauInfo ton="alerte" titre={`Pas encore activable : ${p.activable.raisons.length} point(s) à lever`}>
              <details><summary style={{ cursor: 'pointer' }}>Voir les points</summary>
                <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>{p.activable.raisons.map((x, i) => <li key={i}>{x.message}</li>)}</ul>
              </details>
            </BandeauInfo>
          )}

        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', margin: '12px 0' }}>
          <Chiffre valeur={p.synthese.confirmees} libelle="Confirmé (texte officiel lu)" ton="succes" />
          <Chiffre valeur={p.synthese.a_confirmer} libelle="À confirmer (juriste, convention)" ton="alerte" />
          <Chiffre valeur={decisionsARenseigner.length} libelle="Décisions SOGAS à renseigner" />
          <Chiffre valeur={s ? (s.non_calculables + s.sous_reserve) : '—'} libelle="Salariés avec une donnée manquante ou à confirmer" />
          <Chiffre valeur={s ? (s.a_jour ? 'À jour' : 'À refaire') : 'Non simulé'} libelle={s ? `Effet simulé le ${formaterDate(s.cree_le)}` : 'Effet simulé'} />
        </div>

        <Carte titre="Fiche du pack" Icone={Landmark}>
          <dl style={{ display: 'grid', gridTemplateColumns: 'minmax(140px, max-content) 1fr', gap: '6px 16px', margin: 0, fontSize: 14 }}>
            <dt>Source principale</dt><dd style={{ margin: 0 }}>{p.source_reference || '—'}{p.date_publication ? ` (publiée le ${formaterDate(p.date_publication)})` : ''}</dd>
            <dt>Textes complémentaires</dt><dd style={{ margin: 0 }}>{(p.references || []).length ? <ul style={{ margin: 0, paddingLeft: 18 }}>{p.references.map((t) => <li key={t}>{t}</li>)}</ul> : '—'}</dd>
            <dt>Niveaux des règles</dt><dd style={{ margin: 0 }}>{Object.entries(p.synthese.par_niveau).map(([n, k]) => `${NIVEAUX.find(([x]) => x === n)?.[1] || n} : ${k}`).join(' · ')}</dd>
            <dt>Effet sur les calculs</dt><dd style={{ margin: 0 }}>{p.synthese.appliquees_par_le_moteur} règle(s) déjà lue(s) par un calcul ; {p.synthese.sans_effet_moteur} sans effet aujourd'hui (moteurs à venir)</dd>
            <dt>Motif</dt><dd style={{ margin: 0 }}>{p.motif}</dd>
            {p.commentaire && <><dt>Commentaire</dt><dd style={{ margin: 0 }}>{p.commentaire}</dd></>}
            <dt>Préparé par</dt><dd style={{ margin: 0 }}>{p.auteur || '—'}{p.valideur ? ` · validé par ${p.valideur} le ${formaterDate(p.date_validation)}` : ''}</dd>
            <dt>Historique</dt><dd style={{ margin: 0 }}><Link to="/parametres/journal">Journal des règles</Link></dd>
          </dl>
        </Carte>

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', margin: '12px 0' }}>
          {p.peut_preparer && <Bouton Icone={Plus} onClick={() => setSous('ajouter')}>Ajouter une règle</Bouton>}
          {p.peut_simuler && p.regles.length > 0 && <Bouton Icone={PlayCircle} disabled={enCours}
            onClick={() => agir(() => ecrireCfg('post', `/packs/${id}/simuler`, {}), 'Simulation calculée : aucune donnée réelle n\'a été modifiée.')}>Simuler</Bouton>}
          {p.peut_preparer && <Bouton variante="primaire" Icone={Send} disabled={enCours || !p.activable.possible}
            title={p.activable.possible ? undefined : 'Des règles restent à confirmer'}
            onClick={() => agir(() => ecrireCfg('post', `/packs/${id}/soumettre`), 'Pack simulé et soumis à validation.')}>Soumettre à validation</Bouton>}
          {p.peut_valider && <Bouton variante="primaire" onClick={() => setSous('valider')}>Valider le pack</Bouton>}
          {p.statut_registre === 'A_VALIDER' && droits?.valider && <Bouton variante="danger" onClick={() => setSous('refuser')}>Refuser</Bouton>}
          {['BROUILLON', 'A_VALIDER'].includes(p.statut_registre) && (droits?.preparer || droits?.valider) && <Bouton variante="discret" onClick={() => setSous('annuler')}>Annuler le pack</Bouton>}
        </div>

        <Onglets onglets={onglets} actif={onglet} onChange={setOnglet} libelle="Contenu du pack" />
        <div role="tabpanel" id={`panneau-${onglet}`} aria-labelledby={`onglet-${onglet}`} style={{ marginTop: 12 }}>
          {onglet === 'regles' && domaines.map((d) => (
            <Carte key={d} titre={LIBELLE_DOMAINE[d] || d}>
              <div className="rh-conteneur-tableau"><table className="rh-tableau">
                <thead><tr><th>Règle</th><th>Valeur</th><th>Niveau</th><th>Statut</th><th>Source</th><th>Effet sur les calculs</th>{p.peut_preparer && <th aria-label="Action" />}</tr></thead>
                <tbody>{p.regles.filter((r) => r.domaine === d).map((r) => <LigneRegle key={r.id} r={r} peutRetirer={p.peut_preparer} onRetirer={(x) => setSous({ retirer: x })} />)}</tbody>
              </table></div>
            </Carte>
          ))}

          {onglet === 'sogas' && (
            <Carte titre="Décisions qui reviennent à SOGAS">
              <p style={{ margin: '0 0 8px', fontSize: 14 }}>Ces règles ne viennent ni de la loi ni d'une convention. Elles ne font pas partie du pack : SOGAS les décide et les règle dans leur domaine. Rien n'est inventé ici.</p>
              <div className="rh-conteneur-tableau"><table className="rh-tableau">
                <thead><tr><th>Règle</th><th>Domaine</th><th>Nature</th><th>État</th></tr></thead>
                <tbody>{p.decisions_sogas.map((d) => (
                  <tr key={d.code}>
                    <td><Link to={`/parametres/${SLUG_DOMAINE[d.domaine]}`}>{d.libelle}</Link></td><td>{LIBELLE_DOMAINE[d.domaine]}</td><td>{d.nature}</td>
                    <td>{d.reglee ? <Badge ton="succes">{d.etat}</Badge> : <Badge ton="alerte">{d.etat}</Badge>}</td>
                  </tr>
                ))}</tbody>
              </table></div>
            </Carte>
          )}

          {onglet === 'donnees' && (!s ? <BandeauInfo titre="Pas encore simulé">La simulation mesure la qualité des données des salariés : lancez-la.</BandeauInfo> : (
            <>
              <Carte titre="Qualité des dates d'entrée">
                <ul style={{ margin: 0, paddingLeft: 18, fontSize: 14 }}>{Object.entries(s.qualite_dates_entree || {}).map(([k, n]) => <li key={k}>{QUALITES[k] || k} : {n}</li>)}</ul>
              </Carte>
              <Carte titre="Anomalies de données (aucune correction automatique)">
                <ul style={{ margin: 0, paddingLeft: 18, fontSize: 14 }}>{Object.entries(s.anomalies_donnees || {}).filter(([, n]) => n !== null).map(([k, n]) => <li key={k}>{ANOMALIES[k] || k} : {n}</li>)}</ul>
              </Carte>
              <Carte titre="Données nécessaires, règle par règle">
                <ul style={{ margin: 0, paddingLeft: 18, fontSize: 14 }}>{p.regles.filter((r) => r.donnees.length).map((r) => <li key={r.id}>{r.libelle} : {r.donnees.map((x) => x.libelle).join(' ; ')}</li>)}</ul>
              </Carte>
            </>
          ))}

          {onglet === 'simulation' && (!s ? <BandeauInfo titre="Pas encore simulé">La simulation applique le pack aux salariés en lecture seule : aucune donnée réelle n'est modifiée.</BandeauInfo> : (
            <>
              {!s.a_jour && <BandeauInfo ton="alerte" titre="Simulation à refaire">Les règles ont changé depuis le calcul.</BandeauInfo>}
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', margin: '0 0 12px' }}>
                <Chiffre valeur={s.salaries_analyses} libelle="Salariés analysés" />
                <Chiffre valeur={s.calculables} libelle="Calculables" ton="succes" />
                <Chiffre valeur={s.sous_reserve} libelle="Calculables sous réserve" ton="alerte" />
                <Chiffre valeur={s.non_calculables} libelle="Non calculables" ton="danger" />
              </div>
              <Carte titre="Pourquoi certains salariés ne sont pas calculables">
                <ul style={{ margin: 0, paddingLeft: 18, fontSize: 14 }}>{Object.entries(s.causes_blocage || {}).map(([k, n]) => <li key={k}>{CAUSES[k] || k} : {n}</li>)}</ul>
                {Object.keys(s.reserves_individuelles || {}).length > 0 && <>
                  <p style={{ margin: '8px 0 4px', fontWeight: 600, fontSize: 14 }}>Réserves</p>
                  <ul style={{ margin: 0, paddingLeft: 18, fontSize: 14 }}>{Object.entries(s.reserves_individuelles).map(([k, n]) => <li key={k}>{RESERVES[k] || k} : {n}</li>)}</ul>
                </>}
                <p style={{ margin: '8px 0 4px', fontWeight: 600, fontSize: 14 }}>Pour tous les salariés</p>
                <ul style={{ margin: 0, paddingLeft: 18, fontSize: 14 }}>{(s.reserves_generales || []).map((t) => <li key={t}>{t}</li>)}</ul>
              </Carte>
              <Carte titre="Ancienneté">
                <p style={{ margin: '0 0 6px', fontSize: 14 }}>Majoration à l'ouverture du prochain droit{s.anciennete.bareme_statut !== 'CONFIRME' ? ' (barème non confirmé : indicatif)' : ''} :</p>
                <ul style={{ margin: 0, paddingLeft: 18, fontSize: 14 }}>{Object.entries(s.anciennete.majoration_a_l_ouverture || {}).map(([k, n]) => <li key={k}>{k === 'aucune' ? 'Aucune majoration' : k} : {n} salarié(s)</li>)}</ul>
                <p style={{ margin: '8px 0 4px', fontSize: 14 }}>Paliers franchis dans les 12 prochains mois : {Object.entries(s.anciennete.franchissements_12_mois || {}).map(([k, n]) => `${k} : ${n}`).join(' · ') || 'aucun'}</p>
              </Carte>
              {(s.conflits || []).length > 0 && (
                <Carte titre="Conflits entre règles">
                  <ul style={{ margin: 0, paddingLeft: 18, fontSize: 14 }}>{s.conflits.map((c, i) => <li key={i}>{c.regle} : {c.message}</li>)}</ul>
                </Carte>
              )}
              <Carte titre="Exemples : pourquoi ce résultat ?">
                {(s.exemples || []).map((x) => <Explication key={x.matricule} exemple={x} />)}
              </Carte>
            </>
          ))}
        </div>

        {sous === 'ajouter' && <FenetreAjouterRegle pack={p} onFermer={() => setSous(null)} onTermine={() => fini('Règle ajoutée au pack (brouillon). Relancez la simulation.')} />}
        {sous?.retirer && <FenetreMotifCfg titre={`Retirer « ${sous.retirer.libelle} » du pack`} aide="La règle est annulée dans le pack ; sa trace reste dans le journal." libelleBouton="Retirer"
          onFermer={() => setSous(null)} onConfirmer={async (motif) => { await ecrireCfg('post', `/packs/${id}/regles/${sous.retirer.id}/retirer`, { motif }); await fini('Règle retirée du pack.'); }} />}
        {sous === 'valider' && <FenetreValider titre={`Valider ${p.libelle} — version ${p.version}`} onFermer={() => setSous(null)}
          onValider={async (commentaire) => { await ecrireCfg('post', `/packs/${id}/valider`, { commentaire, confirmation: true }); await fini('Pack validé : ses règles s\'appliquent automatiquement à la date d\'effet.'); }} />}
        {sous === 'refuser' && <FenetreMotifCfg titre="Refuser le pack" aide="Le pack et ses règles reviennent en préparation." onFermer={() => setSous(null)}
          onConfirmer={async (motif) => { await ecrireCfg('post', `/packs/${id}/refuser`, { motif }); await fini('Pack renvoyé en préparation.'); }} />}
        {sous === 'annuler' && <FenetreMotifCfg titre="Annuler le pack" onFermer={() => setSous(null)}
          onConfirmer={async (motif) => { await ecrireCfg('post', `/packs/${id}/annuler`, { motif }); await fini('Pack annulé.'); }} />}
      </div>
    </Layout>
  );
}
